import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, FileWarningIcon, Loader2Icon, PencilIcon, ShieldCheckIcon } from 'lucide-react';
import { PortalHeader } from '../components/shell/PortalHeader';
import { JourneyStages } from '../components/shell/JourneyStages';
import { ComplianceFooter } from '../components/shell/ComplianceFooter';
import { Button } from '../components/ui/Button';
import { HelpPanel } from '../components/ui/HelpPanel';
import { maskSsn } from '../components/ui/SsnField';
import { usePortal } from '../contexts/PortalContext';
import { useTokens } from '../hooks/useTokens';
const ease = [0.23, 1, 0.32, 1] as const;
const processingSteps = ['Confirming your identity', 'Securely retrieving your credit report', 'Matching you with loan options', 'Preparing your options'];

// PLACEHOLDER — final authorization language is supplied by the subscriber's compliance team.
const authorizationTemplate = ['By checking the box below, I authorize {{brokerCompanyName}} ({{brokerNmls}}) and its loan officer {{loanOfficerName}} to obtain a consumer credit report about me from one or more consumer reporting agencies to evaluate my mortgage application.', 'I understand this authorization may extend to verifying information, quality control, and servicing of any loan I obtain, and that a credit inquiry may appear on my credit report.'];

// PLACEHOLDER — credit-impact guidance must be supplied and approved by compliance. Not legal or credit advice.
const creditImpactPlaceholder = 'Your loan team will explain how a credit check may affect your credit before you agree.';
function maskDob(iso: string): string {
  return iso ? `••/••/${iso.slice(0, 4)}` : '';
}
export function Credit() {
  const navigate = useNavigate();
  const {
    answers,
    authorizeCredit
  } = usePortal();
  const {
    fill
  } = useTokens();
  const [phase, setPhase] = useState<'consent' | 'processing'>('consent');
  const [confirmed, setConfirmed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [done, setDone] = useState(0);
  useEffect(() => {
    if (phase !== 'processing') return;
    if (done >= processingSteps.length) {
      const t = window.setTimeout(() => navigate('/options'), 600);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setDone(d => d + 1), 1100);
    return () => window.clearTimeout(t);
  }, [phase, done, navigate]);
  const identity = [{
    label: 'Name',
    value: [answers.firstName, answers.middleName, answers.lastName, answers.suffix].filter(Boolean).join(' ')
  }, {
    label: 'Date of birth',
    value: maskDob(answers.dob)
  }, {
    label: 'Social Security number',
    value: maskSsn(answers.ssn)
  }, {
    label: 'Current address',
    value: `${answers.currentStreet}, ${answers.currentCity}, ${answers.currentState} ${answers.currentZip}`
  }];
  const missing = identity.some(r => !r.value);
  const edit = () => navigate('/apply/contact?from=credit');
  return <div className="min-h-screen w-full bg-canvas" data-id="element-784" data-mp="20s6kafayedvr">
      <PortalHeader maxWidth="max-w-5xl" data-id="element-785" data-mp="20qdd8ywfhrn8" />
      <AnimatePresence mode="wait" data-id="element-786" data-mp="1vstp979xi0i3">
        {phase === 'consent' ? <motion.div key="consent" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25,
        ease
      }} data-id="element-787" data-mp="1vqqy2jx1i1bs">
            <main className="mx-auto max-w-[600px] px-5 pb-10 pt-8 md:pt-14" data-id="element-788" data-mp="2ogfxisu3ptba">
              <JourneyStages current={1} currentProgress={40} data-id="element-789" data-mp="2jhwasq7oodhv" />

              <h1 className="mt-10 text-[28px] font-semibold leading-tight tracking-[-0.025em] text-ink-900 md:text-[36px]" data-id="element-790" data-mp="2jglbb2l9trwk">Authorize a credit check</h1>
              <p className="mt-3 text-[17px] leading-relaxed text-ink-500" data-id="element-791" data-mp="2agoj4qzoaawr">This step is your permission only. You won’t see a score here — your Loan Officer will walk you through your credit results.</p>

              {/* Step 1 — confirm identity from the application on file */}
              <section className="mt-8 rounded-2xl bg-white ring-1 ring-ink-200" aria-labelledby="confirm-id" data-id="element-792" data-mp="2mz0lxzi31b7m">
                <header className="flex items-center gap-3 px-5 pt-5" data-id="element-793" data-mp="2rr0rc7gx8wbl">
                  <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-semibold ${confirmed ? 'bg-positive-500 text-white' : 'bg-ink-900 text-white'}`} aria-hidden data-id="element-794" data-mp="2dyud4qxbwzes">
                    {confirmed ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-795" data-mp="35p8mc9uednk4" /> : '1'}
                  </span>
                  <h2 id="confirm-id" className="flex-1 font-semibold text-ink-900" data-id="element-796" data-mp="3apczpr00e264">
                    Confirm your information
                  </h2>
                  {confirmed && <button onClick={() => setConfirmed(false)} className="text-sm font-medium text-pulse-700 hover:underline" data-id="element-797" data-mp="31nwv5zvvku9t">
                      Change
                    </button>}
                </header>
                <dl className="grid gap-x-6 gap-y-3 px-5 pb-5 pt-4 sm:grid-cols-2" data-id="element-798" data-mp="1qa1fmq126ji2">
                  {identity.map(r => <div key={r.label} className={r.label === 'Current address' ? 'sm:col-span-2' : ''} data-id="element-799" data-mp="1hq68bvvztp2g">
                      <dt className="text-[13px] text-ink-500" data-id="element-800" data-mp="1dsgs49v85o4z">{r.label}</dt>
                      <dd className={`tabular mt-0.5 text-[15px] ${r.value ? 'text-ink-900' : 'font-medium text-critical-700'}`} data-id="element-801" data-mp="1rpn9q147pgsm">{r.value || 'Missing'}</dd>
                    </div>)}
                </dl>
                <AnimatePresence initial={false} data-id="element-802" data-mp="yzf1b0zhaar8">
                  {!confirmed && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease
              }} className="overflow-hidden" data-id="element-803" data-mp="u480q21i9m6j">
                      <div className="flex flex-col gap-2 border-t border-ink-100 p-4 sm:flex-row" data-id="element-804" data-mp="13yhvtrhu0yky">
                        <Button className="sm:flex-1" disabled={missing} onClick={() => setConfirmed(true)} icon={<CheckIcon className="h-4 w-4" data-id="element-806" data-mp="bbneh3gbafbp" />} data-id="element-805" data-mp="g8x9cymgzj6x">
                          Everything looks right
                        </Button>
                        <Button variant="secondary" onClick={edit} icon={<PencilIcon className="h-4 w-4" data-id="element-808" data-mp="2gbon03yed27t" />} data-id="element-807" data-mp="2gczqs3rue9yx">
                          Edit
                        </Button>
                      </div>
                      {missing && <p className="px-5 pb-4 text-sm text-critical-700" data-id="element-809" data-mp="2p5tb4fm5bzjn">Add the missing details before continuing.</p>}
                    </motion.div>}
                </AnimatePresence>
              </section>

              {/* Step 2 — authorization */}
              <section className={`mt-4 rounded-2xl bg-white ring-1 ring-ink-200 transition-opacity duration-200 ${confirmed ? '' : 'pointer-events-none opacity-50'}`} aria-labelledby="auth-lang" aria-disabled={!confirmed} data-id="element-810" data-mp="21fz105t7op33">
                <header className="flex items-center gap-3 px-5 pt-5" data-id="element-811" data-mp="3cvxf4o38y1b6">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-ink-900 text-[11px] font-semibold text-white" aria-hidden data-id="element-812" data-mp="3hu8nsvht25rt">
                    2
                  </span>
                  <h2 id="auth-lang" className="flex-1 font-semibold text-ink-900" data-id="element-813" data-mp="3s005vk930mkm">
                    Give permission
                  </h2>
                </header>
                <div className="mx-5 mt-4 rounded-xl bg-ink-50 p-4" data-id="element-814" data-mp="3i6t5yex41yrx">
                  <p className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-caution-50 px-2.5 py-1 text-[11px] font-medium text-caution-700" data-id="element-815" data-mp="3e7k9h2aormso">
                    <FileWarningIcon className="h-3 w-3" aria-hidden data-id="element-816" data-mp="395l981a8t5zy" /> Placeholder copy · pending compliance review
                  </p>
                  <div className="max-h-40 space-y-3 overflow-y-auto text-[13px] leading-relaxed text-ink-500" tabIndex={0} data-id="element-817" data-mp="2z8yn7966k8wc">
                    {authorizationTemplate.map(p => <p key={p.slice(0, 24)} data-id="element-818" data-mp="xhvbshoguqaq">{fill(p)}</p>)}
                  </div>
                </div>
                <label className="flex cursor-pointer items-start gap-3 p-5" data-id="element-819" data-mp="tg9zoyjrag1l">
                  <input type="checkbox" checked={agreed} disabled={!confirmed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 h-5 w-5 rounded accent-pulse-600" data-id="element-820" data-mp="1cpjlokxt8jj7" />
                  <span className="text-[15px] text-ink-900" data-id="element-821" data-mp="1gspvg2k4ssuf">I’ve read and agree to the credit authorization above.</span>
                </label>
              </section>

              <div className="mt-4" data-id="element-822" data-mp="17x083nz4hoee">
                <HelpPanel question="Will this affect my credit?" data-id="element-823" data-mp="t1aofwhetncu">
                  <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-caution-50 px-2.5 py-1 text-[11px] font-medium text-caution-700" data-id="element-824" data-mp="szzibq0hs3ik">
                    <FileWarningIcon className="h-3 w-3" aria-hidden data-id="element-825" data-mp="k1e4u99vneon" /> Placeholder copy · pending compliance review
                  </span>
                  <span className="block" data-id="element-826" data-mp="ozz3i49hdqaz">{creditImpactPlaceholder}</span>
                </HelpPanel>
              </div>

              <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-827" data-mp="2ecsw4uijdm6i">
                <Button variant="ghost" icon={<ArrowLeftIcon className="h-4 w-4" data-id="element-829" data-mp="2tiq8763k21kk" />} onClick={() => navigate('/apply/review')} data-id="element-828" data-mp="2thf5l9luizv1">
                  Back to review
                </Button>
                <Button size="lg" disabled={!confirmed || !agreed} icon={<ShieldCheckIcon className="h-5 w-5" data-id="element-831" data-mp="2xja280su0p2h" />} onClick={() => {
              authorizeCredit();
              setPhase('processing');
            }} data-id="element-830" data-mp="2ogr6s1rdleq9">
                  Authorize credit check
                </Button>
              </div>
            </main>
            <ComplianceFooter className="max-w-[600px]" data-id="element-832" data-mp="3l9ct4dl0fozi" />
          </motion.div> : <motion.main key="processing" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col items-center justify-center px-6 py-16 text-center" aria-live="polite" data-id="element-833" data-mp="3h6pyk4wyfpym">
            <PulseLoader data-id="element-834" data-mp="1mvb8fn017oic" />
            <h1 className="mt-10 text-[28px] font-semibold tracking-[-0.025em] text-ink-900" data-id="element-835" data-mp="2r22utrqsh1um">Checking your loan options…</h1>
            <p className="mt-2 text-ink-500" data-id="element-836" data-mp="2n0hiq8m2wrlh">Authorization received. This usually takes less than a minute.</p>
            <ol className="mt-10 w-full space-y-3 text-left" data-id="element-837" data-mp="2i2fr2zal7jye">
              {processingSteps.map((s, i) => {
            const state = i < done ? 'done' : i === done ? 'active' : 'waiting';
            return <motion.li key={s} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: state === 'waiting' ? 0.4 : 1,
              y: 0
            }} transition={{
              duration: 0.25,
              delay: i * 0.05
            }} className="flex items-center gap-3 text-[15px]" data-id="element-838" data-mp="3apilpgc57ajd" data-mpi={i}>
                    <span className={`grid h-6 w-6 place-items-center rounded-full ${state === 'done' ? 'bg-positive-500 text-white' : 'bg-ink-100 text-ink-500'}`} data-id="element-839" data-mp="2w1fgi95xc2vm">
                      {state === 'done' ? <motion.span initial={{
                  scale: 0.5
                }} animate={{
                  scale: 1
                }} transition={{
                  duration: 0.18
                }} data-id="element-840" data-mp="3oqcw24ir3lko">
                          <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-841" data-mp="3w213p80rvqpw" />
                        </motion.span> : state === 'active' ? <Loader2Icon className="h-3.5 w-3.5 animate-spin text-pulse-600" data-id="element-842" data-mp="9u15v3rjy27t" /> : null}
                    </span>
                    <span className={state === 'done' ? 'text-ink-500' : 'text-ink-900'} data-id="element-843" data-mp="eu5qn5zhtj3u">{s}</span>
                  </motion.li>;
          })}
            </ol>
          </motion.main>}
      </AnimatePresence>
    </div>;
}
function PulseLoader() {
  return <div className="relative grid h-28 w-28 place-items-center" data-id="element-844" data-mp="sk953as4s8iq">
      {[0, 1].map(i => <motion.span key={i} className="absolute inset-0 rounded-full border border-pulse-300" initial={{
      scale: 0.7,
      opacity: 0.8
    }} animate={{
      scale: 1.25,
      opacity: 0
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeOut',
      delay: i
    }} aria-hidden data-id="element-845" data-mp="xjvhji04xkqh" />)}
      <div className="grid h-20 w-20 place-items-center rounded-full bg-pulse-600 shadow-lift" data-id="element-846" data-mp="33kxuhddpzjpf">
        <svg viewBox="0 0 48 24" className="h-8 w-12" fill="none" aria-hidden data-id="element-847" data-mp="33ik4hixwcxoq">
          <motion.path d="M2 12h10l4-9 6 18 5-13 3 4h16" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: [0, 1, 1],
          opacity: [1, 1, 0]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'linear',
          times: [0, 0.7, 1]
        }} data-id="element-848" data-mp="2yno5fyu8ta24" />
        </svg>
      </div>
    </div>;
}